// Static registry of "Claude AI Developer V2" (CD02) course lessons.
//
// V2 is a SEPARATE course from CD01, not a modification of it. CD01
// (pages/claudeDevCourse/devLessonsRegistry.js) is untouched and still live;
// V2 is tester-only while it's being evaluated. Because it's a clean course, its
// lesson IDs and routes are numbered in true display order — there are no legacy
// IDs to work around.
//
// What V2 changes, per the offer plan (AlexHormoziPlanForCodenec_UPDATED.docx):
//
//  1. Students build their real app DURING the course rather than after it.
//     Module 1 opens with two new lessons — name your idea (V2101), then see an
//     app with your name on it running (V2102).
//  2. It teaches WEB APPLICATION development. Almost every app this audience
//     wants — a menu with ordering, a booking page, a client tracker — is a web
//     app, so Module 3 teaches HTML/CSS/JavaScript on a live page instead of
//     CD01's Python console exercises, and Module 4 grows the actual app rather
//     than printing to stdout. Everything is front-end + localStorage, so it
//     runs in the browser, works on a phone, and ships as a static site.
//
// 38 lessons vs. CD01's 30 — plus Module 6's 11, added later, for 49 in total.
//
// `layout` decides which lesson layout renders (UI only):
//   "canvas"    — Lesson | (Concept Canvas + Prompt Lab).
//   "promptLab" — Lesson | Prompt Lab | Editor or Rubric panel.
//   "editor"    — Lesson | Editor | Console (slim "Ask Claude").  [CD01 only]
//   "template"  — Lesson | (brand form + live Starter App Template preview).
//   "webLab"    — Lesson | HTML editor | live page preview + checks.
//   "hotspot"   — Lesson | (question strip + a replica of the real app to click).
//   "checklist" — Lesson | (step checklist + Prompt Lab). Tracks work done OUTSIDE
//                 the app; self-attested, not graded.
//
// `grading` decides how the lesson is assessed (authoritative; decoupled from layout):
//   "canvas"   — interactive self-check, no AI grade.
//   "rubric"   — the student's conversation is graded against a rubric by an AI judge.
//   "tests"    — the student's code is run against deterministic stdin/stdout test cases.
//   "template" — self-check: they branded the Starter App Template and ran it.
//   "dom"      — assertions run against the rendered page inside the preview iframe.
//
// `captureProject: true` adds the "My App Idea" card to the Prompt Lab, which
// saves the student's own app to `users_projects`. From then on every lesson chat
// in the course is personalized to it (see promptLabClaudeController).
//
// `lessonID` MUST match the Firestore `lessons` document ID — it keys the
// per-user content cache. V2 ids are distinct from CD01's, so the two courses
// track progress and cache content completely independently.
//
// DEF_VERSION: bump whenever the generated lesson schema or grading changes, so
// per-user cached definitions are treated as stale and regenerated on next load.
export const DEF_VERSION = 3;

export const CLAUDE_DEV_V2_COURSE_LESSONS = [
    // Module 1 — Your idea, and your first win (day one)
    { lessonID: "V2101", route: "V2_M1_L1", title: "Choose your idea: what do you want to build?", layout: "promptLab", type: "concept", grading: "rubric", captureProject: true },
    { lessonID: "V2102", route: "V2_M1_L2", title: "Your app, running today: make the Starter App Template yours", layout: "template", type: "concept", grading: "template" },

    // …then Foundations: How Software Actually Works (interactive canvas self-check)
    { lessonID: "V2103", route: "V2_M1_L3", title: "What software is and what a developer actually does", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V2104", route: "V2_M1_L4", title: "How a program runs (input → logic → output)", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V2105", route: "V2_M1_L5", title: "Frontend vs. backend vs. database — the three-part picture", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V2106", route: "V2_M1_L6", title: "The three languages of the web: HTML, CSS and JavaScript", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V2107", route: "V2_M1_L7", title: "The developer's toolkit: editor, terminal, browser", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V2108", route: "V2_M1_L8", title: "The single most important skill: breaking a problem into steps", layout: "canvas", type: "concept", grading: "canvas" },

    // Module 2 — Working With Claude as a Developer (conversation graded by rubric)
    { lessonID: "V2201", route: "V2_M2_L1", title: "What Claude can and can't do for a developer", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V2202", route: "V2_M2_L2", title: "Anatomy of a good coding prompt (context, goal, constraints)", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V2203", route: "V2_M2_L3", title: "Reading Claude's response: code, explanation, and assumptions", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V2204", route: "V2_M2_L4", title: "Iterating: \"that's close, now change this\"", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V2205", route: "V2_M2_L5", title: "Giving Claude an error message and getting unstuck", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V2206", route: "V2_M2_L6", title: "Asking Claude to explain code you don't understand", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V2207", route: "V2_M2_L7", title: "When to trust the AI and when to verify", layout: "promptLab", type: "concept", grading: "rubric" },

    // Module 3 — The web page and how it works (live preview, DOM checks)
    // Replaces CD01's Python console module: same underlying concepts, taught on a
    // real page so they transfer straight into the app they're building.
    { lessonID: "V2301", route: "V2_M3_L1", title: "HTML: the structure of a page", layout: "webLab", type: "concept", grading: "dom" },
    { lessonID: "V2302", route: "V2_M3_L2", title: "CSS: colours, text and spacing", layout: "webLab", type: "concept", grading: "dom" },
    { lessonID: "V2303", route: "V2_M3_L3", title: "Layout: arranging things on the page", layout: "webLab", type: "concept", grading: "dom" },
    { lessonID: "V2304", route: "V2_M3_L4", title: "Variables and data in JavaScript", layout: "webLab", type: "concept", grading: "dom" },
    { lessonID: "V2305", route: "V2_M3_L5", title: "Making decisions with if / else", layout: "webLab", type: "concept", grading: "dom" },
    { lessonID: "V2306", route: "V2_M3_L6", title: "Lists and loops — showing many items", layout: "webLab", type: "concept", grading: "dom" },
    { lessonID: "V2307", route: "V2_M3_L7", title: "Functions — reusable actions in JavaScript", layout: "webLab", type: "concept", grading: "dom" },
    { lessonID: "V2308", route: "V2_M3_L8", title: "Buttons, forms and events: making the page respond", layout: "webLab", type: "concept", grading: "dom" },
    { lessonID: "V2309", route: "V2_M3_L9", title: "Reading an error in the browser console without panicking", layout: "webLab", type: "concept", grading: "dom" },

    // Module 4 — Build your app (planning → rubric, building → live page + DOM checks)
    // The project was already chosen in Module 1, so this opens on specifying it.
    // The build lessons grow the SAME app they branded in V2102 — see
    // starterTemplate/authoredLessons.js, which supplies each lesson's starting point.
    // The other half of V2101. This lesson is graded against what Module 1 already
    // settled as well as its own conversation, so it spends its time on the parts of
    // a spec the student has NOT pinned down yet — see
    // components/PromptLabHub/buildSpecLesson.js. `downloadOnPerfect` is the reward
    // for filling every slot: their branded app, with the finished spec inside it.
    { lessonID: "V2401", route: "V2_M4_L1", title: "Turning your idea into a build spec with Claude", layout: "promptLab", type: "concept", grading: "rubric", downloadOnPerfect: true },
    { lessonID: "V2402", route: "V2_M4_L2", title: "Planning the pieces: pages, data and actions", layout: "promptLab", type: "concept", grading: "rubric" },
    // `downloadable: true` turns on the Web Lab "Download my app" button, which saves the
    // student's HTML as index.html so they can keep building their app locally with Claude.
    { lessonID: "V2403", route: "V2_M4_L3", title: "Building your app's main screen", layout: "webLab", type: "concept", grading: "dom", downloadable: true },
    { lessonID: "V2404", route: "V2_M4_L4", title: "Making it interactive: buttons and forms", layout: "webLab", type: "concept", grading: "dom", downloadable: true },
    { lessonID: "V2405", route: "V2_M4_L5", title: "Saving your data so it's still there tomorrow", layout: "webLab", type: "concept", grading: "dom", downloadable: true },
    { lessonID: "V2406", route: "V2_M4_L6", title: "Showing your saved data as a list", layout: "webLab", type: "concept", grading: "dom", downloadable: true },
    { lessonID: "V2407", route: "V2_M4_L7", title: "Adding an AI feature to your app", layout: "webLab", type: "concept", grading: "dom", downloadable: true },
    { lessonID: "V2408", route: "V2_M4_L8", title: "Testing each piece as you go", layout: "promptLab", type: "concept", grading: "rubric" },

    // Module 5 — Ship it: your app, live
    //
    // This module exists to end the course at a URL the student can send to a
    // customer. Not "you could deploy it" — deployed. It was six lessons of
    // reflection (debugging, reading documentation, a rubric-graded chat ABOUT
    // publishing) and is now Git → GitHub → a public address, done for real on
    // their own machine. Four of the old six were cut: debugging is already taught
    // by V2205 and V2309, "putting your app online" is replaced by V2513 actually
    // putting it online, and the phone lesson's one real payload — open it on your
    // phone — is the second-to-last tick of V2513, where they finally have a URL a
    // phone can reach.
    //
    // WHY GITHUB PAGES. The `git push` IS the deploy, so version control and
    // shipping are one skill instead of two. No CLI, no card, no billing console,
    // and it serves https, so connecting Firebase later still works. The project
    // zip's links are all relative (`href="app.html"`), so it runs unchanged from a
    // /repo-name/ subpath.
    //
    // EVERY CHECKLIST BRANCHES ON `claudecode` vs `browser`. Module 6 gates on a
    // PAID Claude plan but its checklists are self-attested, so students without one
    // still arrive here. The browser path does the whole module through github.com —
    // web uploader, web editor, Pages toggle — and lands on the same live URL. The
    // course's promise cannot depend on a subscription we don't sell.
    //
    // WHY THE NUMBERING JUMPS TO 11. V2502/V2503/V2504/V2506 are RETIRED — this
    // module replaced them — and V2507/V2508 with routes V2_M5_L7/L8 are RESERVED by
    // the "Git lesson arc" in `git stash@{0}`, which has its own pair of hotspot
    // lessons under those ids and has already pregenerated them.
    //
    // That reservation is not bookkeeping. `lesson_content` is one global collection
    // keyed on the lesson id alone, shared by every course and every branch, and the
    // client's cache check compares only `defVersion` and `locale` — never the id or
    // the title it belongs to. So two lessons sharing an id do not conflict loudly;
    // one silently serves the other's prose beside the wrong screen. That is exactly
    // what happened here before the renumber. Reusing a retired id has the same
    // failure, because the retired lesson's cached content outlives its registry
    // entry. NEVER recycle a CD02 lesson id — take the next free number.
    { lessonID: "V2501", route: "V2_M5_L1", title: "Version control: the undo history for your whole app", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V2511", route: "V2_M5_L11", title: "Put your app on GitHub", layout: "checklist", type: "concept", grading: "canvas" },
    // Doing → checklist, reading a window → hotspot (Module 6's rule). V2511 puts the
    // repo there; this reads it, and doubles as the map for V2513's Settings → Pages.
    { lessonID: "V2512", route: "V2_M5_L12", title: "Reading your project on GitHub", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V2513", route: "V2_M5_L13", title: "Publish it: your app gets a real address", layout: "checklist", type: "concept", grading: "canvas" },
    // A deploy you can only do once is a demo. This is the loop they keep.
    { lessonID: "V2514", route: "V2_M5_L14", title: "Ship a change: the loop from now on", layout: "checklist", type: "concept", grading: "canvas" },
    // Reads last despite route L5 — order is array position, not the route number.
    { lessonID: "V2505", route: "V2_M5_L5", title: "Where to go next and how to keep learning", layout: "promptLab", type: "concept", grading: "rubric" },

    // Module 6 — Claude Code: coding on your own computer
    //
    // The first time the course leaves the browser. Everything before this runs in
    // a sandboxed iframe; here the student installs a real tool and points it at
    // the `index.html` they downloaded in Module 4 (the payoff for `downloadable`).
    //
    // It sits LAST on purpose. Cross-module gating means it only unlocks once they
    // have shipped something, which is the first moment installing developer tooling
    // is worth the friction — and Claude Code needs a PAID Claude plan (Pro, Max,
    // Team, Enterprise or Console credits; the free plan excludes it), so nobody
    // should hit that wall before they have a finished app in hand.
    //
    // These lessons describe an external product whose install steps, pricing, model
    // list and on-screen controls change without notice, so NOTHING student-facing
    // here is generated. The prose is authored in
    // backend/src/data/claudeCodeLessonProse.js and pinned server-side; the canvases
    // in authoredCanvases.js (V2601 is pinned despite being "explore" mode, because
    // a generated canvas would invent UI parts).
    //
    // SEVEN of the eleven are `hotspot` lessons whose screen is a hand-built REPLICA of
    // one part of the app — the tab bar, the folder picker, the session screen, the
    // model menu, the effort menu, the mode selector, the diff. One replica per
    // control is the point: a single screenshot-sized mock taught none of them,
    // because "how do I change the model" is a menu you open, not a paragraph.
    //
    // The student is given the FINISHED window and answers a short series of
    // questions by clicking the right part of it. They were Web Labs until the
    // exercise was reworked: that version had them typing the answers into the
    // replica's own HTML, which drilled a Module 3 skill to teach a Module 6 one and
    // printed every answer on screen beside the row it belonged to.
    //
    // Every replica lives in starterTemplate/authoredLessons.js and is built on the
    // shared `claudeCodeScreen` helper, so the whole module restyles in one place.
    // UPKEEP: when the real app moves, that file and the prose file are the pair to
    // fix, and both name each other.
    //
    // These eleven and Module 5's V2511–V2514 are the only CD02 lessons routed from
    // this registry rather than from a hand-written component file — see
    // pages/claudeDevV2Course/V2LessonPage.js.
    { lessonID: "V2601", route: "V2_M6_L1", title: "Meet Claude Code: Claude on your own computer", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V2602", route: "V2_M6_L2", title: "Download and install the Claude desktop app", layout: "checklist", type: "concept", grading: "canvas" },
    { lessonID: "V2603", route: "V2_M6_L3", title: "The three tabs: Chat, Cowork and Code", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V2604", route: "V2_M6_L4", title: "Add your project: Local and Select folder", layout: "hotspot", type: "concept", grading: "dom" },
    // Reads as lesson 5 despite the id and route saying 11: order comes from position
    // in THIS array, not from either number (V2505 sits LAST in Module 5 for the same
    // reason). Inserting it this way leaves L5–L10's routes — and every users_lessons
    // progress doc keyed on them — untouched.
    //
    // V2604 shows the new-session screen and grades recognition of it; this is where
    // the student does it on their own machine. It is the module's second `checklist`
    // because that work happens in Finder or Explorer, where nothing we render can
    // follow it — same reason as V2602, and the reason both are self-attested.
    { lessonID: "V2611", route: "V2_M6_L11", title: "Open your own app in Claude Code", layout: "checklist", type: "concept", grading: "canvas" },
    { lessonID: "V2605", route: "V2_M6_L5", title: "Reading the session screen", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V2606", route: "V2_M6_L6", title: "Choosing a model, and changing it mid-session", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V2607", route: "V2_M6_L7", title: "Effort level: how hard Claude works on a task", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V2608", route: "V2_M6_L8", title: "Permission modes: how much it does without asking", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V2609", route: "V2_M6_L9", title: "Reviewing a change before it touches your files", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V2610", route: "V2_M6_L10", title: "CLAUDE.md, and working on your app safely", layout: "promptLab", type: "concept", grading: "rubric" },
];

// Every lesson carries the course's DEF_VERSION so consumers can check a cached
// definition against the version of the course it belongs to. PromptLabHubComponent
// is shared by CD01 and CD02, which version independently — it must not assume
// either course's constant.
CLAUDE_DEV_V2_COURSE_LESSONS.forEach((l) => {
    l.defVersion = DEF_VERSION;
});

export const getV2LessonMetaByRoute = (route) =>
    CLAUDE_DEV_V2_COURSE_LESSONS.find((l) => l.route === route);

/**
 * Given a route, return the route of the next lesson in the registry, or null
 * if this is the last one.
 */
export const getNextV2LessonRoute = (currentRoute) => {
    const idx = CLAUDE_DEV_V2_COURSE_LESSONS.findIndex((l) => l.route === currentRoute);
    if (idx === -1 || idx === CLAUDE_DEV_V2_COURSE_LESSONS.length - 1) return null;
    return CLAUDE_DEV_V2_COURSE_LESSONS[idx + 1].route;
};

/**
 * Given a route, return the route of the previous lesson in the registry, or null
 * if this is the first one (the footer's Back button then falls back to the ToC).
 */
export const getPrevV2LessonRoute = (currentRoute) => {
    const idx = CLAUDE_DEV_V2_COURSE_LESSONS.findIndex((l) => l.route === currentRoute);
    if (idx <= 0) return null;
    return CLAUDE_DEV_V2_COURSE_LESSONS[idx - 1].route;
};

/**
 * Position of a lesson within its module, for a "Lesson X of Y" progress label.
 * The module number is parsed from the V2_M{n}_L{k} route and the total counts
 * lessons sharing that module. Returns null for unknown/non-V2 routes.
 */
export const getV2LessonProgress = (route) => {
    const m = /^V2_M(\d+)_L/.exec(route || "");
    if (!m) return null;
    const moduleNum = Number(m[1]);
    const moduleLessons = CLAUDE_DEV_V2_COURSE_LESSONS.filter((l) =>
        l.route.startsWith(`V2_M${moduleNum}_L`)
    );
    const lessonInModule = moduleLessons.findIndex((l) => l.route === route) + 1;
    if (lessonInModule === 0) return null;
    return { moduleNum, lessonInModule, totalInModule: moduleLessons.length };
};
